import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Form,
  Input,
  Select,
  Descriptions,
  Spin,
  Button,
  Table,
  InputNumber,
  message,
} from 'antd';
import {
  cartonApi,
  supplierApi,
  customerApi,
  productApi,
  type CartonDTO,
  type CartonProductItemDTO,
  type ProductDTO,
  type UpdateCartonRequest,
} from '../api/kfc';

const STATE_LABELS: Record<string, string> = {
  preparing: '待收货',
  inKFYWarehouse: '抵达凯飞越仓库',
  inAgentWarehouse: '抵达代理仓库(出发地)',
  readyForShipping: '准备运输',
  onTheWay: '在途运输',
  arrivedDstPort: '抵达目的港',
  recevied: '已签收',
};

/** 状态历程 */
const StatesTimeline: React.FC<{
  states: Record<string, Record<string, unknown>>;
  stateLabels: Record<string, string>;
}> = ({ states, stateLabels }) => {
  const keys = Object.keys(states).sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    if (Number.isNaN(na)) return 1;
    if (Number.isNaN(nb)) return -1;
    return String(a).localeCompare(String(b));
  });
  return (
    <div style={{ marginTop: 8 }}>
      {keys.map((key) => {
        const node = states[key] as Record<string, unknown> | undefined;
        if (!node) return null;
        const state = (node.state as string) || '';
        const ts = node.timestamp as number | undefined;
        const operationLog = node.operationLog as Record<string, unknown> | undefined;
        const label = stateLabels[state] || state;
        return (
          <div key={key} style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>{key}. {label}</div>
            {ts != null && <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>时间：{new Date(ts).toLocaleString()}</div>}
            {state === 'inKFYWarehouse' && operationLog && typeof operationLog === 'object' && (
              <div style={{ fontSize: 12, color: '#333', marginTop: 8 }}>
                <>
                  {operationLog.arrivedBy !== undefined && <div>到达方式：{Number(operationLog.arrivedBy) === 0 ? '货拉拉' : Number(operationLog.arrivedBy) === 1 ? '快递' : String(operationLog.arrivedBy)}</div>}
                  {operationLog.queryParamOfShippment != null && String(operationLog.queryParamOfShippment) && <div>查询单号：{String(operationLog.queryParamOfShippment)}</div>}
                  {operationLog.pictures && typeof operationLog.pictures === 'object' && Object.keys(operationLog.pictures as Record<string, string>).length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ marginBottom: 4 }}>照片：</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {Object.entries(operationLog.pictures as Record<string, string>).map(([desc, base64]) => (
                          <div key={desc} style={{ textAlign: 'center' }}>
                            <img src={base64} alt={desc} style={{ maxWidth: 120, maxHeight: 90, objectFit: 'contain', border: '1px solid #eee', borderRadius: 4 }} />
                            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{desc || '照片'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ProductListInDrawer: React.FC<{
  products: CartonProductItemDTO[];
  onRemove: (index: number) => void;
  onAmountChange: (index: number, amount: number) => void;
  fetchProductByHscode: (hscode: string) => Promise<ProductDTO | null>;
}> = ({ products, onRemove, onAmountChange, fetchProductByHscode }) => {
  const [details, setDetails] = useState<Record<string, ProductDTO | null>>({});
  useEffect(() => {
    products.forEach((p) => {
      const hscode = p.hscode;
      if (!hscode) return;
      fetchProductByHscode(hscode).then((d) => setDetails((prev) => ({ ...prev, [hscode]: d })));
    });
  }, [products, fetchProductByHscode]);

  const columns = [
    { title: '海关编码', dataIndex: 'hscode', key: 'hscode', width: 120, ellipsis: true },
    {
      title: '产品信息',
      key: 'detail',
      ellipsis: true,
      render: (_: unknown, record: CartonProductItemDTO) => {
        const d = record.hscode ? details[record.hscode] : undefined;
        if (d === undefined) return <span style={{ color: '#999' }}>加载中…</span>;
        if (d === null) return <span style={{ color: '#999' }}>未找到该海关编码对应的产品档案</span>;
        const name = d.nameZN || d.nameEN || '-';
        const brand = d.brand ? `品牌：${d.brand}` : '';
        const price = d.price ? `USD ${d.price.USD ?? '-'} / RMB ${d.price.RMB ?? '-'}` : '';
        return (
          <div style={{ fontSize: 12 }}>
            <div>名称：{name}</div>
            {brand && <div>{brand}</div>}
            {price && <div>价格：{price}</div>}
          </div>
        );
      },
    },
    {
      title: '数量',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (val: number | undefined, _: CartonProductItemDTO, index: number) => (
        <InputNumber min={1} value={val ?? 0} onChange={(v) => onAmountChange(index, v ?? 1)} size="small" style={{ width: 72 }} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_: unknown, __: CartonProductItemDTO, index: number) => (
        <Button type="link" danger size="small" onClick={() => onRemove(index)}>删除</Button>
      ),
    },
  ];

  const dataSource = products.map((p, index) => ({ ...p, key: `${p.hscode}-${index}` }));
  if (!products.length) return <div style={{ color: '#999', marginTop: 8 }}>暂无产品，请从上方选择并添加</div>;
  return <Table size="small" dataSource={dataSource} columns={columns} pagination={false} scroll={{ x: 400 }} />;
};

export interface CartonDetailDrawerProps {
  open: boolean;
  cartonId: string | undefined;
  onClose: () => void;
  onSaved?: () => void;
}

/** 货物详情滑窗（统一样式、带关闭按钮），可复用于运单详情中的货物详情 */
const CartonDetailDrawer: React.FC<CartonDetailDrawerProps> = ({ open, cartonId, onClose, onSaved }) => {
  const [detailCarton, setDetailCarton] = useState<CartonDTO | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailForm] = Form.useForm();
  const [suppliers, setSuppliers] = useState<{ value: string; label: string }[]>([]);
  const [customers, setCustomers] = useState<{ value: string; label: string }[]>([]);
  const [productOptions, setProductOptions] = useState<ProductDTO[]>([]);
  const [productDetailCache, setProductDetailCache] = useState<Record<string, ProductDTO | null>>({});
  const [selectedProductToAdd, setSelectedProductToAdd] = useState<string | null>(null);

  const fetchProductByHscode = useCallback((hscode: string): Promise<ProductDTO | null> => {
    if (!hscode?.trim()) return Promise.resolve(null);
    if (productDetailCache[hscode] !== undefined) return Promise.resolve(productDetailCache[hscode]);
    return productApi.getByHscode(hscode).then((r) => r.data).catch(() => null).then((p) => {
      setProductDetailCache((prev) => ({ ...prev, [hscode]: p }));
      return p;
    });
  }, [productDetailCache]);

  useEffect(() => {
    supplierApi.list().then((res) => {
      setSuppliers((res.data || []).map((s) => ({ value: s.id!, label: `${s.supplierName || s.supplierId || s.id}` })));
    }).catch(() => setSuppliers([]));
    customerApi.list().then((res) => {
      setCustomers((res.data || []).map((c) => ({ value: c.id!, label: `${c.name || c.customerCode || c.id}` })));
    }).catch(() => setCustomers([]));
    productApi.list().then((res) => setProductOptions(res.data || [])).catch(() => setProductOptions([]));
  }, []);

  useEffect(() => {
    if (!open || !cartonId) {
      setDetailCarton(null);
      return;
    }
    setDetailLoading(true);
    setDetailCarton(null);
    cartonApi
      .get(cartonId)
      .then((res) => {
        const c = res.data;
        setDetailCarton(c);
        detailForm.setFieldsValue({
          cartonId: c?.cartonId,
          fromSupplierId: c?.fromSupplierId,
          belongClientId: c?.belongClientId,
          shipmentId: c?.shipmentId ?? '',
        });
      })
      .catch(() => message.warning('加载货物详情失败'))
      .finally(() => setDetailLoading(false));
  }, [open, cartonId, detailForm]);

  const saveDetail = () => {
    if (!detailCarton?.id) return;
    detailForm.validateFields().then((values) => {
      setDetailSaving(true);
      const products: CartonProductItemDTO[] = (detailCarton.products || []).map((p) => ({ hscode: p.hscode, amount: p.amount ?? 0 }));
      const body: UpdateCartonRequest = {
        cartonId: values.cartonId,
        fromSupplierId: values.fromSupplierId || undefined,
        belongClientId: values.belongClientId || undefined,
        shipmentId: values.shipmentId?.trim() || undefined,
        products: products.length ? products : undefined,
      };
      cartonApi
        .update(detailCarton.id!, body)
        .then(() => {
          message.success('保存成功');
          setDetailCarton((prev) => (prev ? { ...prev, ...values, shipmentId: values.shipmentId?.trim() || undefined } : null));
          onSaved?.();
        })
        .catch((e) => message.error(e.response?.data?.message || '保存失败'))
        .finally(() => setDetailSaving(false));
    });
  };

  const addProductToDetail = (product: ProductDTO, amount: number = 1) => {
    const hscode = product.hscode;
    if (!hscode) return;
    const exists = (detailCarton?.products || []).some((p) => p.hscode === hscode);
    if (exists) {
      message.info('该产品已在列表中');
      return;
    }
    setSelectedProductToAdd(null);
    setDetailCarton((prev) => (prev ? { ...prev, products: [...(prev.products || []), { hscode, amount }] } : prev));
  };

  const removeProductFromDetail = (index: number) => {
    setDetailCarton((prev) => {
      if (!prev?.products?.length) return prev;
      const next = [...prev.products];
      next.splice(index, 1);
      return { ...prev, products: next };
    });
  };

  const setDetailProductAmount = (index: number, amount: number) => {
    setDetailCarton((prev) => {
      if (!prev?.products?.[index]) return prev;
      const next = [...prev.products];
      next[index] = { ...next[index], amount };
      return { ...prev, products: next };
    });
  };

  const renderState = (state: string) => STATE_LABELS[state] || state;

  return (
    <Drawer
      title="货物详情"
      placement="right"
      width="50%"
      open={open}
      onClose={onClose}
      destroyOnClose
      closable
      extra={
        <Button type="primary" loading={detailSaving} onClick={saveDetail}>
          保存
        </Button>
      }
    >
      {detailLoading ? (
        <Spin spinning />
      ) : detailCarton ? (
        <>
          <Form form={detailForm} layout="vertical" style={{ marginBottom: 24 }}>
            <Form.Item name="cartonId" label="货物标识" rules={[{ required: true }]}>
              <Input placeholder="货物标识" />
            </Form.Item>
            <Form.Item name="fromSupplierId" label="供应商">
              <Select placeholder="选择供应商" allowClear showSearch optionFilterProp="label" options={suppliers} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="belongClientId" label="客户">
              <Select placeholder="选择客户" allowClear showSearch optionFilterProp="label" options={customers} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="shipmentId" label="运单号">
              <Input placeholder="运单号" />
            </Form.Item>
          </Form>
          <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="状态">{renderState(detailCarton.state || '')}</Descriptions.Item>
          </Descriptions>
          <h4 style={{ marginBottom: 8 }}>产品列表</h4>
          <div style={{ marginBottom: 8 }}>
            <Select
              placeholder="从已有产品中选择并添加"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              value={selectedProductToAdd}
              onChange={(hscode) => {
                const p = productOptions.find((o) => o.hscode === hscode);
                if (p) addProductToDetail(p);
              }}
              options={productOptions.map((p) => ({ value: p.hscode, label: `${p.hscode} ${p.nameZN || p.nameEN || ''}`.trim() }))}
            />
          </div>
          <ProductListInDrawer
            products={detailCarton.products || []}
            onRemove={removeProductFromDetail}
            onAmountChange={setDetailProductAmount}
            fetchProductByHscode={fetchProductByHscode}
          />
          <h4 style={{ marginTop: 24, marginBottom: 8 }}>状态历程</h4>
          {detailCarton.states && Object.keys(detailCarton.states).length > 0 ? (
            <StatesTimeline states={detailCarton.states} stateLabels={STATE_LABELS} />
          ) : (
            <div style={{ color: '#999', fontSize: 12 }}>暂无状态节点</div>
          )}
        </>
      ) : null}
    </Drawer>
  );
};

export default CartonDetailDrawer;
